import { useId, useState, type ReactNode } from 'react';
import { sortChoices, type SortWording, type TableSort } from '../../utils/tableSort';
import { Icon } from './Icon';
import { Select } from './primitives';
import { cx } from './primitives/cx';

/**
 * P-076 (P1) · La lista densa y su fila compacta: lo que en el teléfono sustituye a una tabla de escritorio
 * cuando la tarea es «identificar un registro y actuar» (un catálogo, un historial, los productos de una
 * venta). Una fila es título + línea secundaria a la izquierda, importe o estado a la derecha, cifras de
 * apoyo en una segunda línea, y lo secundario detrás de «Ver detalles» (con teclado y `aria-expanded`).
 * Las acciones de la fila siguen en la fila: no se esconden en un menú.
 *
 * No es una tarjeta por registro: es una lista con filas separadas por una línea, para que quepan varias
 * por pantalla. Las tablas que sirven para COMPARAR cifras (analíticas) no usan esto: siguen siendo tablas
 * (`n3-table--analytic`, `styles/density.css`).
 */
export interface DenseFigure { label: ReactNode; value: ReactNode }

export interface DenseRowProps {
  title: ReactNode;
  subtitle?: ReactNode;
  /** Datos cortos en una línea («g · Peso · ×1000»). */
  meta?: ReactNode[];
  /** El importe o la cifra que manda, a la derecha. */
  value?: ReactNode;
  valueTone?: 'default' | 'muted' | 'danger' | 'success' | 'warning';
  /** La insignia de estado, bajo el valor. */
  badge?: ReactNode;
  /** Cifras de apoyo en una segunda línea («Esperado $4,952 · Contado $4,900»). */
  figures?: DenseFigure[];
  /** Lo secundario, detrás de «Ver detalles» (normalmente una `MetaList`). */
  details?: ReactNode;
  detailsLabel?: string;
  /** Cómo se llama este registro para el lector de pantalla («Ver detalles de Cono sencillo»). */
  name?: string;
  actions?: ReactNode;
  inactive?: boolean;
  flagged?: boolean;
  defaultOpen?: boolean;
  className?: string;
}

export function DenseRow({ title, subtitle, meta, value, valueTone = 'default', badge, figures, details, detailsLabel = 'Ver detalles', name, actions, inactive = false, flagged = false, defaultOpen = false, className }: DenseRowProps) {
  const [open, setOpen] = useState(defaultOpen);
  const detailsId = useId();
  const hasDetails = details !== undefined && details !== null && details !== false;
  const hasActions = actions !== undefined && actions !== null && actions !== false;
  return (
    <li className={cx('n3-row', inactive && 'n3-row--inactive', flagged && 'n3-row--flagged', className)}>
      <div className="n3-row__main">
        <div className="n3-row__title">{title}</div>
        {subtitle && <div className="n3-row__subtitle">{subtitle}</div>}
        {meta && meta.length > 0 && <div className="n3-row__meta">{meta.map((item, index) => <span key={index}>{item}</span>)}</div>}
      </div>
      {(value !== undefined || badge) && (
        <div className="n3-row__aside">
          {value !== undefined && <span className={cx('n3-row__value', valueTone !== 'default' && `n3-row__value--${valueTone}`)}>{value}</span>}
          {badge}
        </div>
      )}
      {figures && figures.length > 0 && (
        <div className="n3-row__figures">
          {figures.map((figure, index) => <span className="n3-row__figure" key={index}>{figure.label} <strong>{figure.value}</strong></span>)}
        </div>
      )}
      {(hasDetails || hasActions) && (
        <div className={cx('n3-row__foot', !hasDetails && 'n3-row__foot--actions-only')}>
          {hasDetails && (
            <button
              aria-controls={detailsId}
              aria-expanded={open}
              aria-label={`${open ? 'Ver menos' : detailsLabel}${name ? ` de ${name}` : ''}`}
              className="n3-row__toggle"
              onClick={() => setOpen((current) => !current)}
              type="button"
            >
              {open ? 'Ver menos' : detailsLabel}
              <Icon name={open ? 'chevron-up' : 'chevron-down'} size={14} />
            </button>
          )}
          {hasActions && <div className="n3-row__actions">{actions}</div>}
        </div>
      )}
      {hasDetails && open && <div className="n3-row__details" id={detailsId}>{details}</div>}
    </li>
  );
}

/**
 * El orden de la lista en el teléfono: un desplegable con las mismas claves y direcciones que los
 * encabezados de la tabla de escritorio (`sortChoices` garantiza que el orden actual siempre está).
 */
export interface DenseSort<K extends string> {
  wording: SortWording<K>;
  choices: ReadonlyArray<readonly [K, 'asc' | 'desc']>;
  value: TableSort<K>;
  onChange: (next: TableSort<K>) => void;
  label?: string;
}

export function DenseList<K extends string = string>({ label, children, plain = false, separated = false, sort, className }: {
  /** Nombre accesible de la lista («Catálogo de productos»). */
  label: string;
  children: ReactNode;
  /** Dentro de una tarjeta o de una hoja: sin fondo ni sombra propios. */
  plain?: boolean;
  /**
   * P-079 · Registros separados: cada fila con su contorno fino, radio y 8 px de hueco con la siguiente, sin sombra ni
   * más relleno. Para las listas de página cuyas filas son altas (título, cifras, insignias y pie: Historial de cortes,
   * Traspasos, Insumos…), donde con la línea fina de 1 px el pie de una se fundía con el título de la siguiente. No se
   * combina con `plain`: una sublista corta dentro de una hoja se lee con la línea.
   */
  separated?: boolean;
  sort?: DenseSort<K>;
  className?: string;
}) {
  return (
    <div className={cx('n3-list', plain && 'n3-list--plain', separated && !plain && 'n3-list--separated', className)}>
      {sort && (
        <div className="n3-list__tools">
          <label className="n3-sort">
            <span className="n3-sort__label">{sort.label ?? 'Ordenar'}</span>
            <Select
              onChange={(event) => { const [key, dir] = event.target.value.split(':') as [K, 'asc' | 'desc']; sort.onChange({ key, dir }); }}
              value={`${sort.value.key}:${sort.value.dir}`}
            >
              {sortChoices(sort.wording, sort.choices, sort.value).map((choice) => <option key={choice.value} value={choice.value}>{choice.label}</option>)}
            </Select>
          </label>
        </div>
      )}
      <ul aria-label={label} className="n3-list__items">{children}</ul>
    </div>
  );
}
